import * as React from 'react'

import { i18n } from '@thg-commerce/enterprise-core'
import { ProductBlock } from '@thg-commerce/enterprise-components'
import { AmpCarouselProps } from '@thg-commerce/gravity-patterns/Carousel/types'
import { Carousel } from '@thg-commerce/gravity-patterns'
import { pushToDataLayer } from '@thg-commerce/enterprise-metrics/src/data_layer'
import { ProductPriceProps } from '@thg-commerce/gravity-elements'

export enum RecommendationTrackingContext {
  DEFAULT = 'default',
  BASKET = 'bkt',
}

export interface ProductRecommendationsProps {
  products: {
    url: string
    title: string
    brand: {
      name: string
    }
    sku: number
    price: ProductPriceProps
    image: string
    reviews: {
      averageScore: number
      total: number
    }
  }[]
  itemsPerSlide: number | number[]
  trackingContext?: RecommendationTrackingContext
}

export const ProductRecommendations = (props: ProductRecommendationsProps) => {
  React.useEffect(() => {
    pushToDataLayer({
      type: 'elysiumEvent',
      eventData: {
        eventAction: 'Viewed',
        eventCategory: 'Product | Recommendations',
        eventLabel: 'Recommendations rails component',
      },
    })
  }, [])

  const i18nText = {
    buttonI18nText: {
      buyAriaLabel: i18n('general.productbuynow.label'),
      buyText: i18n('general.productbuynow.text'),
      quickBuyText: i18n('general.product.quickbuy.text'),
    },
    reviewsI18nText: {
      reviewsStarsLabel: i18n('reviews.stars.label'),
      reviewsLabel: i18n('reviews.reviews.label'),
    },
    carouselText: {
      leftScrollLabel: i18n('carousel.controls.left.arialabel'),
      rightScrollLabel: i18n('carousel.controls.right.arialabel'),
    },
  }

  const ampProps: AmpCarouselProps = {
    height: 500,
    width: 300,
    type: 'slides',
    role: 'region',
  }

  const trackProductClick = (index: number) => {
    pushToDataLayer({
      type: 'elysiumEvent',
      eventData: {
        eventAction: 'Clicked',
        eventCategory: 'Product | Recommendations',
        eventLabel: 'Recommendations product clicked position',
        eventLabelValue: `${index}`,
      },
    })
  }

  const trackQuickBuyClick = (index: number) => {
    pushToDataLayer({
      type: 'elysiumEvent',
      eventData: {
        eventAction: 'Clicked',
        eventCategory: 'Product | Recommendations',
        eventLabel: 'Recommendations product buy position',
        eventLabelValue: `${index}`,
      },
    })
  }

  const productBlocks = props.products.map((product, index) => (
    <ProductBlock
      sku={product.sku.toString()}
      title={{ value: product.title, useAlternateStyle: false }}
      brand={product.brand}
      url={
        props.trackingContext
          ? `${product.url}?rctxt=${props.trackingContext}`
          : product.url
      }
      image={{
        lazy: true,
        isAmp: false,
        urls: { largeProduct: product.image },
      }}
      price={product.price}
      review={{
        starRating: product.reviews.averageScore,
        numberOfReviews: product.reviews.total,
        screenReaderOnlyText: `${product.reviews.averageScore} ${i18nText.reviewsI18nText.reviewsStarsLabel} 
    ${product.reviews.total} ${i18nText.reviewsI18nText.reviewsLabel}`,
      }}
      // button={{
      //   title: i18nText.buttonI18nText.buyText,
      //   ariaLabel: vsprintf(i18nText.buttonI18nText.buyAriaLabel, [
      //     product.title,
      //   ]),
      //   quickBuyTitle: i18nText.buttonI18nText.quickBuyText,
      // }}
      key={`${product.title}-${index}`}
      fromRecommendations={true}
      onClickEventEmitter={() => trackProductClick(index)}
      onQuickBuyClickEventEmitter={() => trackQuickBuyClick(index)}
    />
  ))

  return (
    <Carousel
      items={productBlocks}
      itemsPerSlide={props.itemsPerSlide}
      i18n={i18nText.carouselText}
      isAmp={false}
      ampProps={ampProps}
      hideControlsOnSingleSlide={true}
      hideControls={false}
      indicatorStyle="letterbox"
    />
  )
}
